// ProjectCard.jsx
import React from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  Event,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Assignment,
  CloudUpload,
} from "@mui/icons-material";
import dateFormat from "dateformat";
import ProjectDescriptionAccordion from "./ProjectDescriptionAccordion";

// Helper to get color and icon for status chip
const getStatusProps = (status) => {
  switch (status) {
    case "submitted":
      return { label: "Submitted", color: "#2563eb", bg: "#e0e7ff", icon: <CheckCircle /> };
    case "completed":
      return { label: "Completed", color: "#eab308", bg: "#fef9c3", icon: <CheckCircle /> };
    case "rejected":
      return { label: "Rejected", color: "#ef4444", bg: "#fee2e2", icon: <Cancel /> };
    default:
      return { label: "Pending", color: "#14558e", bg: "transparent", icon: <HourglassEmpty /> };
  }
};


const ProjectCard = ({ project, onInitiateSubmission }) => {
  const statusProps = getStatusProps(project.status);
  const isSubmissionDisabled =
    project.status === "submitted" ||
    project.status === "completed" ||
    project.status === "rejected";

  // Example logo color (replace with your logo color logic if needed)
  const logoColor = "#7c3aed"; // Premium purple
  const logoBg = "linear-gradient(90deg, #f68139, #ff9960)";

  // Progress calculation (dummy, replace with real if available)
  const progress = project.progress || 0; // 0-100

  return (
    <Box
      sx={{
        borderRadius: 4,
        mb: 3,
        // boxShadow: "0 6px 24px 0 rgba(124,58,237,0.10)",
        background: "#fff",
        border: `#dbcbcb 1px solid`,
        p: { xs: 2, sm: 3 },
        maxWidth: 600,
        mx: "auto",
        position: "relative",
        overflow: "hidden",
        transition: "box-shadow 0.2s",
        ':hover': {
          boxShadow: "0 8px 32px 0 rgba(228, 126, 71, 0.18)",
        },
      }}
    >
      {/* Progress Bar */}
      <Box sx={{ width: "100%", position: "absolute", top: 0, left: 0 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 5,
            borderRadius: 2,
            background: logoBg,
            '& .MuiLinearProgress-bar': {
              background: logoColor,
            },
          }}
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, mt: 1 }}>
        <Typography
          variant="h6"
          fontWeight={800}
          sx={{
            color: "#0e0e0e",
            letterSpacing: 0.2,
            fontSize: { xs: "1.1rem", sm: "1.25rem" },
          }}
        >
          {project.title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            background: statusProps.bg,
            color: statusProps.color,
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            fontWeight: 700,
            fontSize: "0.98rem",
            boxShadow: "0 2px 8px 0 rgba(0,0,0,0.04)",
            minWidth: 90,
            justifyContent: "center",
          }}
        >
          {statusProps.icon}
          <span style={{ marginLeft: 6 }}>{statusProps.label}</span>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          color: "#64748b",
          fontSize: "1rem",
          mb: 1.5,
        }}
      >
        {project.deadline && (
          <Box sx={{ display: "flex", alignItems: "center", mr: 3 }}>
            <Event sx={{ fontSize: 18, mr: 0.5, color: logoColor }} />
            <span style={{ fontWeight: 600, marginRight: 4 }}>Deadline:</span>
            {dateFormat(project.deadline, "dS mmm, h:MM TT")}
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Assignment sx={{ fontSize: 18, mr: 0.5, color: "#242222"}} />
          <span style={{ fontWeight: 600, marginRight: 4 }}>Marks:</span>
          {project.marks}
        </Box>
      </Box>
      {project.description && (
        <ProjectDescriptionAccordion description={project.description} />
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          href={project.resources_url}
          target="_blank"
          sx={{
            borderRadius: 2,
            borderColor: logoColor,
            color: logoColor,
            fontWeight: 700,
            px: 2.5,
            py: 1,
            textTransform: "none",
            fontSize: "1rem",
            boxShadow: "none",
            ':hover': {
              borderColor: logoColor,
              background: logoBg,
            },
          }}
        >
          View Statement
        </Button>
        <Button
          variant="contained"
          startIcon={<CloudUpload />}
          disabled={isSubmissionDisabled}
          onClick={() => onInitiateSubmission({ id: project.id })}
          sx={{
            borderRadius: 2,
            background: isSubmissionDisabled ? statusProps.bg : logoBg,
            color: isSubmissionDisabled ? statusProps.color : "#fff",
            fontWeight: 700,
            px: 2.5,
            py: 1,
            textTransform: "none",
            fontSize: "1rem",
            boxShadow: "none",
            ':hover': {
              background: isSubmissionDisabled ? statusProps.bg : "#e99235",
            },
          }}
        >
          {statusProps.label === "Submitted" ? "Submitted" : statusProps.label === "Completed" ? "Completed" : statusProps.label === "Rejected" ? "Submission Locked" : "Submit Project"}
        </Button>
      </Box>
      {/* Responsive styles */}
      <style>{`
        @media (max-width: 600px) {
          .MuiBox-root {
            max-width: 98vw !important;
            padding: 1rem !important;
          }
        }
      `}</style>
    </Box>
  );
};

export default ProjectCard;